import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MappingRule } from '@/types';
import { toast } from 'sonner';

interface AddMappingRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddRule: (rule: Omit<MappingRule, 'id'>) => void;
  editingRule?: MappingRule | null;
}

export const AddMappingRuleDialog: React.FC<AddMappingRuleDialogProps> = ({
  open,
  onOpenChange,
  onAddRule,
  editingRule,
}) => {
  const [formData, setFormData] = React.useState({
    mainAccountFrom: '',
    mainAccountTo: '',
    teamFrom: '',
    teamTo: '',
    projectFrom: '',
    projectTo: '',
  });

  const [errors, setErrors] = React.useState({
    mainAccount: '',
    team: '',
    project: '',
  });

  useEffect(() => {
    if (editingRule) {
      setFormData({
        mainAccountFrom: editingRule.mainAccountFrom,
        mainAccountTo: editingRule.mainAccountTo,
        teamFrom: editingRule.teamFrom || '',
        teamTo: editingRule.teamTo || '',
        projectFrom: editingRule.projectFrom || '',
        projectTo: editingRule.projectTo || '',
      });
    } else {
      setFormData({
        mainAccountFrom: '',
        mainAccountTo: '',
        teamFrom: '',
        teamTo: '',
        projectFrom: '',
        projectTo: '',
      });
    }
    setErrors({
      mainAccount: '',
      team: '',
      project: '',
    });
  }, [editingRule, open]);

  const validateForm = () => {
    const newErrors = {
      mainAccount: '',
      team: '',
      project: '',
    };

    // Validate main account (required)
    if (!formData.mainAccountFrom || !formData.mainAccountTo) {
      newErrors.mainAccount = 'Both Account From and To are required';
    }

    // Validate team (if one is filled, both must be filled)
    if ((formData.teamFrom && !formData.teamTo) || (!formData.teamFrom && formData.teamTo)) {
      newErrors.team = 'Both Team From and To must be filled if one is provided';
    }

    // Validate project (if one is filled, both must be filled)
    if ((formData.projectFrom && !formData.projectTo) || (!formData.projectFrom && formData.projectTo)) {
      newErrors.project = 'Both Project From and To must be filled if one is provided';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      Object.values(errors).forEach(error => {
        if (error) toast.error(error);
      });
      return;
    }

    onAddRule(formData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Mapping Rule' : 'Add Mapping Rule'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mainAccountFrom">Account From</Label>
                <Input
                  id="mainAccountFrom"
                  value={formData.mainAccountFrom}
                  onChange={(e) =>
                    setFormData({ ...formData, mainAccountFrom: e.target.value })
                  }
                  placeholder="e.g. 1000"
                  required
                  className={errors.mainAccount ? 'border-red-500' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mainAccountTo">Account To</Label>
                <Input
                  id="mainAccountTo"
                  value={formData.mainAccountTo}
                  onChange={(e) =>
                    setFormData({ ...formData, mainAccountTo: e.target.value })
                  }
                  placeholder="e.g. 1999"
                  required
                  className={errors.mainAccount ? 'border-red-500' : ''}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="teamFrom">Team From</Label>
                <Input
                  id="teamFrom"
                  value={formData.teamFrom}
                  onChange={(e) =>
                    setFormData({ ...formData, teamFrom: e.target.value })
                  }
                  placeholder="Optional"
                  className={errors.team ? 'border-red-500' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="teamTo">Team To</Label>
                <Input
                  id="teamTo"
                  value={formData.teamTo}
                  onChange={(e) =>
                    setFormData({ ...formData, teamTo: e.target.value })
                  }
                  placeholder="Optional"
                  className={errors.team ? 'border-red-500' : ''}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="projectFrom">Project From</Label>
                <Input
                  id="projectFrom"
                  value={formData.projectFrom}
                  onChange={(e) =>
                    setFormData({ ...formData, projectFrom: e.target.value })
                  }
                  placeholder="Optional"
                  className={errors.project ? 'border-red-500' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="projectTo">Project To</Label>
                <Input
                  id="projectTo"
                  value={formData.projectTo}
                  onChange={(e) =>
                    setFormData({ ...formData, projectTo: e.target.value })
                  }
                  placeholder="Optional"
                  className={errors.project ? 'border-red-500' : ''}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-[#ff6d00] hover:bg-[#ff6d00]/90 text-white">
              {editingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};